/**
 * Permission Context
 * Manages user authentication and permissions
 * Integrates with Azure Easy Auth (production) and dev bypass (local)
 *
 * All permissions are resolved from roles via /admin/me/resolved-permissions.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiGet, apiPost, clearSelectedTenant } from '../utils/apiClient';
import {
  hasAction,
  PERMISSION_LEVELS,
  ACTIONS,
} from './PermissionConstants';
import { getThemeForUser, loadThemePreference } from '../utils/themeSelector';

const PermissionContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [resolvedPermissions, setResolvedPermissions] = useState({}); // Action-based format from roles
  const [userRoles, setUserRoles] = useState([]); // Assigned roles
  const [userType, setUserType] = useState(null);
  const [linkedInvestorId, setLinkedInvestorId] = useState(null);
  const [userTheme, setUserTheme] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasNoTenantAccess, setHasNoTenantAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isDev =
    process.env.NODE_ENV === 'development' &&
    process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';

  /**
   * Load resolved permissions from the API (always role-based)
   */
  const loadResolvedPermissions = useCallback(async () => {
    try {
      const response = await apiGet('/admin/me/resolved-permissions');
      if (response.success && response.data) {
        setResolvedPermissions(response.data.permissions || {});
        if (response.data.is_admin) {
          setIsAdmin(true);
        }
        return response.data;
      }
    } catch (err) {
      console.warn('Could not load resolved permissions:', err.message);
    }
    return null;
  }, []);

  /**
   * Load user permissions from the API
   */
  const loadUserPermissions = useCallback(async (userEmail) => {
    try {
      // Check if this is an external user (Keycloak)
      const isExternalUser = sessionStorage.getItem('userType') === 'external_client';
      const externalUserData = sessionStorage.getItem('externalUser');

      if (isExternalUser && externalUserData) {
        // External users don't have access to /admin/me - use stored Keycloak data
        const extUser = JSON.parse(externalUserData);
        console.log('Loading external user from Keycloak data:', extUser.email);

        setCurrentUserEmail(extUser.email);
        setResolvedPermissions({});
        setUserType('external_client');
        setLinkedInvestorId(extUser.linked_investor_id || null);
        setIsAuthenticated(true);
        setIsAdmin(false);
        setHasNoTenantAccess(false);
        console.log('External user loaded:', extUser.email, 'Type: external_client');

        // Set theme for external user
        const savedPreference = loadThemePreference();
        const theme = getThemeForUser(extUser.email, savedPreference);
        setUserTheme(theme);

        return true;
      }

      // For internal users, fetch user info from API
      const response = await apiGet('/admin/me');
      const currentUser = response;

      if (!currentUser || !currentUser.email) {
        // User is authenticated but not in authorized_users - they have no tenant access
        console.log(`User ${userEmail} not found in authorized_users - redirecting to no-access page`);
        setCurrentUserEmail(userEmail);
        setIsAuthenticated(true);
        setHasNoTenantAccess(true);
        setResolvedPermissions({});
        setUserType(null);
        setIsAdmin(false);
        return true; // Authentication succeeded, but no permissions
      }

      setCurrentUserEmail(currentUser.email || userEmail);
      setCurrentUserId(currentUser.id || null);
      setUserType(currentUser.userType || currentUser.user_type || 'internal');
      setLinkedInvestorId(currentUser.linkedInvestorId || currentUser.linked_investor_id || null);
      setIsAuthenticated(true);
      setIsAdmin(currentUser.is_admin || false);
      setHasNoTenantAccess(false);
      console.log('User loaded:', currentUser.email, 'is_admin:', currentUser.is_admin, 'Type:', currentUser.userType || currentUser.user_type);

      // Load resolved permissions (role-based) immediately
      // This must complete before render so permissions are set correctly
      try {
        await loadResolvedPermissions();
      } catch (err) {
        console.warn('Failed to load resolved permissions:', err);
      }

      // Determine and set user theme based on email
      const savedPreference = loadThemePreference();
      const theme = getThemeForUser(userEmail, savedPreference);
      setUserTheme(theme);
      console.log('User theme set:', savedPreference || 'auto-detected from email');

      return true;
    } catch (err) {
      console.error('Error loading user permissions:', err);

      // Check for errors that indicate user needs to request access
      const errorData = err.response?.data;
      const errorType = typeof errorData?.error === 'object' ? errorData.error.type : errorData?.error;
      const errorMessage = (typeof errorData?.error === 'object' ? errorData.error.message : errorData?.message) || '';
      const status = err.response?.status;

      // Handle: tenant access denied, no tenant access, or user not found in authorized_users
      if (
        errorType === 'no_tenant_access' ||
        errorType === 'tenant_access_denied' ||
        errorType === 'not_authorized' ||
        (status === 403 && (errorMessage.includes('not authorized') || errorMessage.includes('not found'))) ||
        (status === 401 && (errorMessage.includes('not found') || errorMessage.includes('User not found')))
      ) {
        console.log(`User ${userEmail} not found or no tenant access - redirecting to no-access page`);
        setCurrentUserEmail(userEmail);
        setIsAuthenticated(true);
        setHasNoTenantAccess(true);
        return true;
      }

      setError(err.message);
      return false;
    }
  }, [loadResolvedPermissions]);

  /**
   * Extract email from Easy Auth principal
   */
  const extractEmailFromPrincipal = (principal) => {
    if (principal.user_claims) {
      const emailClaim = principal.user_claims.find(
        c => c.typ === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress' ||
             c.typ === 'email' ||
             c.typ === 'preferred_username'
      );
      if (emailClaim) {
        return emailClaim.val;
      }
    }
    return principal.user_id;
  };

  /**
   * Check for existing session on mount
   */
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        setLoading(true);
        setError(null);

        let userEmail = null;

        // Check for external user session (Keycloak) first
        const externalUserData = sessionStorage.getItem('externalUser');
        const keycloakToken = sessionStorage.getItem('keycloakToken');

        if (externalUserData && keycloakToken) {
          try {
            const extUser = JSON.parse(externalUserData);
            userEmail = extUser.email;
            console.log('Restoring external user session for', userEmail);
            await loadUserPermissions(userEmail);
          } catch (parseError) {
            console.error('Failed to parse external user data:', parseError);
            sessionStorage.removeItem('externalUser');
            sessionStorage.removeItem('keycloakToken');
            sessionStorage.removeItem('keycloakRefreshToken');
            sessionStorage.removeItem('userType');
          }
        } else if (isDev) {
          // Check for stored dev session
          userEmail = sessionStorage.getItem('devUserEmail');

          if (userEmail) {
            console.log('Dev mode: Restoring session for', userEmail);
            await loadUserPermissions(userEmail);
          } else {
            console.log('Dev mode: No existing session, showing login page');
          }
        } else {
          // Production mode - check Azure Easy Auth
          console.log('Production mode: Checking Azure Easy Auth');
          try {
            const response = await fetch('/.auth/me');
            const data = await response.json();

            if (data && data.length > 0) {
              const claims = data[0];
              userEmail = claims.user_id || extractEmailFromPrincipal(claims);

              if (claims.id_token) {
                sessionStorage.setItem('azureAccessToken', claims.id_token);
              }

              if (userEmail) {
                await loadUserPermissions(userEmail);
              }
            }
          } catch (authError) {
            console.log('Not authenticated via Azure Easy Auth');
          }
        }
      } catch (err) {
        console.error('Error checking session:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    checkExistingSession();
  }, [isDev, loadUserPermissions]);

  /**
   * Login function for dev mode
   */
  const login = async (email) => {
    setLoading(true);
    setError(null);

    // Clear any existing session data before loading new user
    sessionStorage.removeItem('devUserEmail');
    sessionStorage.removeItem('keycloakToken');
    sessionStorage.removeItem('keycloakRefreshToken');
    sessionStorage.removeItem('azureAccessToken');
    sessionStorage.removeItem('userType');
    sessionStorage.removeItem('externalUser');
    clearSelectedTenant();

    // Reset state
    setCurrentUserEmail(null);
    setResolvedPermissions({});
    setUserRoles([]);
    setUserType(null);
    setLinkedInvestorId(null);
    setIsAuthenticated(false);
    setIsAdmin(false);
    setHasNoTenantAccess(false);

    try {
      // Set devUserEmail BEFORE API calls so apiClient interceptor sends the correct header
      sessionStorage.setItem('devUserEmail', email);
      const success = await loadUserPermissions(email);
      if (!success) {
        sessionStorage.removeItem('devUserEmail');
      }
      return success;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Login function for external Keycloak users
   */
  const loginAsExternal = async (externalUser) => {
    setLoading(true);
    setError(null);

    try {
      const success = await loadUserPermissions(externalUser.email);
      return success;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Logout function
   */
  const logout = useCallback(() => {
    const wasExternalUser = sessionStorage.getItem('userType') === 'external_client';
    const refreshToken = sessionStorage.getItem('keycloakRefreshToken');

    // Clear session storage
    sessionStorage.removeItem('devUserEmail');
    sessionStorage.removeItem('keycloakToken');
    sessionStorage.removeItem('keycloakRefreshToken');
    sessionStorage.removeItem('azureAccessToken');
    sessionStorage.removeItem('userType');
    sessionStorage.removeItem('externalUser');

    clearSelectedTenant();

    // Reset state
    setCurrentUserEmail(null);
    setResolvedPermissions({});
    setUserRoles([]);
    setUserType(null);
    setLinkedInvestorId(null);
    setIsAuthenticated(false);
    setIsAdmin(false);
    setHasNoTenantAccess(false);
    setUserTheme(null);
    setError(null);

    // Handle logout redirect based on user type
    if (wasExternalUser) {
      if (refreshToken) {
        apiPost('/auth/logout', { refreshToken }).catch(() => {});
      }
    } else if (!isDev) {
      window.location.href = '/.auth/logout?post_logout_redirect_uri=/login';
    }
  }, [isDev]);

  /**
   * Check if user has permission (level-based API surface, role-based internals)
   * Maps legacy level checks to action checks:
   *   ADMIN -> requires is_admin flag
   *   WRITE -> requires create or update action
   *   READ  -> requires read action
   */
  const checkPermission = useCallback((module, level = PERMISSION_LEVELS.READ) => {
    if (isAdmin) return true;
    if (level === PERMISSION_LEVELS.ADMIN) return false;
    if (level === PERMISSION_LEVELS.WRITE) {
      return hasAction(resolvedPermissions, module, 'create') || hasAction(resolvedPermissions, module, 'update');
    }
    return hasAction(resolvedPermissions, module, 'read');
  }, [resolvedPermissions, isAdmin]);

  /**
   * Check if user has a specific action (role-based)
   */
  const checkAction = useCallback((module, action) => {
    if (isAdmin) return true;
    return hasAction(resolvedPermissions, module, action);
  }, [resolvedPermissions, isAdmin]);

  /**
   * Convenience methods for common action checks
   */
  const canRead = useCallback((module) => checkAction(module, ACTIONS.READ), [checkAction]);
  const canCreate = useCallback((module) => checkAction(module, ACTIONS.CREATE), [checkAction]);
  const canUpdate = useCallback((module) => checkAction(module, ACTIONS.UPDATE), [checkAction]);
  const canDelete = useCallback((module) => checkAction(module, ACTIONS.DELETE), [checkAction]);
  const canApprove = useCallback((module) => checkAction(module, ACTIONS.APPROVE), [checkAction]);

  /**
   * Update user theme preference
   */
  const setUserThemePreference = (themeName) => {
    const theme = getThemeForUser(currentUserEmail, themeName);
    setUserTheme(theme);
  };

  /**
   * Refresh resolved permissions (call after role changes)
   */
  const refreshPermissions = useCallback(async () => {
    if (currentUserEmail) {
      await loadResolvedPermissions();
    }
  }, [currentUserEmail, loadResolvedPermissions]);

  const value = {
    // User info
    user: {
      email: currentUserEmail,
      resolvedPermissions: resolvedPermissions,
      roles: userRoles,
      userType: userType,
      linkedInvestorId: linkedInvestorId,
      isExternal: userType === 'external_client',
      isAdmin: isAdmin,
    },
    currentUserEmail,
    currentUserId,
    resolvedPermissions,
    userRoles,
    userType,
    linkedInvestorId,
    userTheme,
    isAuthenticated,
    isAdmin,
    hasNoTenantAccess,
    loading,
    error,

    // Permission checks (level-based API, role-based internals)
    hasPermission: checkPermission,

    // Permission checks (action-based)
    hasAction: checkAction,
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    canApprove,

    // Auth functions
    login,
    loginAsExternal,
    logout,
    reload: () => currentUserEmail && loadUserPermissions(currentUserEmail),
    refreshPermissions,
    setUserThemePreference,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
